import { useEffect, useState } from "react";
import { Check, ChevronDown, Globe2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSite } from "@/hooks/use-site";
import { LOCALES } from "@/lib/locale-registry";
import { locales, localizeUrl, type Locale } from "@/lib/site";
import { cn } from "@/lib/utils";

const labels: Record<Locale, string> = Object.fromEntries(
  LOCALES.map((definition) => [definition.code, definition.label]),
) as Record<Locale, string>;

export function LanguageSwitcher({
  paths,
  home = false,
  triggerClassName,
}: {
  paths?: Partial<Record<Locale, string>>;
  home?: boolean;
  triggerClassName?: string;
}) {
  const { site, messages, href } = useSite();
  const [open, setOpen] = useState(false);
  const pathname = new URL(href, site.origin).pathname;

  useEffect(() => setOpen(false), [href]);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex h-9 items-center gap-2 rounded-lg border border-border bg-background/70 px-3 text-xs font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            triggerClassName,
          )}
          aria-label={`${messages.language}: ${labels[site.locale]}`}
        >
          <Globe2 className="h-3.5 w-3.5 text-muted-foreground" />
          <span>{labels[site.locale]}</span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-40">
        {locales.map((locale) => (
          <DropdownMenuItem key={locale} asChild className="cursor-pointer justify-between gap-4">
            <a
              href={localizeUrl(home ? "/" : (paths?.[locale] ?? pathname), locale, href)}
              aria-current={locale === site.locale ? "page" : undefined}
            >
              {labels[locale]}
              {locale === site.locale && <Check className="h-4 w-4 text-primary" />}
            </a>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
